import { ChevronDown, ChevronUp, Check, Minus, Plus, RefreshCw, X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { ReplanDiff } from '@/lib/tasks';

/**
 * Replan preview + confirmation modal. Used everywhere a Replan
 * action lives in the app — My Home icon, Edit Home sticky bar, Tools
 * page, Settings page. Opens with a PREVIEW (what would change) and
 * a "Replan now" button; user explicitly confirms before any DB
 * write happens. Behaves the same regardless of entry surface.
 *
 * Sections (only rendered when non-empty):
 *   - Items to add — task titles the new plan would generate
 *   - Items to remove — task titles that no longer apply
 *   - Items to reschedule — count only (a wall of "shifted" titles
 *     is just noise)
 *
 * "How does this work?" toggle expands the long-form explanation of
 * what Replan does / preserves / changes. Collapsed by default to
 * keep the preview calm.
 *
 * Empty diff renders a "Nothing to update" state with no Replan
 * button — running a replan with zero deltas would just churn the
 * task table for no user-visible benefit.
 */
export function ReplanSummaryModal({
  visible,
  diff,
  homeName,
  loading = false,
  onClose,
  onConfirm,
}: {
  visible: boolean;
  diff: ReplanDiff | null;
  homeName: string;
  /** True while the preview is being computed. Shows a spinner in
   *  place of the diff sections so the user knows we're working. */
  loading?: boolean;
  onClose: () => void;
  /** Fires when the user taps "Replan now". The caller runs the
   *  actual regen, then closes the modal. */
  onConfirm?: () => void;
}) {
  // "How does this work?" toggle. Collapsed each open so the preview
  // is the first thing the user sees.
  const [showExplain, setShowExplain] = useState(false);
  // Tracks whether the user just confirmed and we're waiting for the
  // regen to finish. Disables the button to prevent double-taps.
  const [confirming, setConfirming] = useState(false);
  const opacity = useSharedValue(0);
  const scale = useSharedValue(0.9);

  useEffect(() => {
    if (visible) {
      opacity.value = withTiming(1, { duration: 180, easing: Easing.out(Easing.cubic) });
      scale.value = withSpring(1, { mass: 0.6, damping: 14, stiffness: 220 });
    } else {
      opacity.value = withTiming(0, { duration: 120 });
      scale.value = withTiming(0.95, { duration: 120 });
      // Reset on close so a re-open starts clean.
      setShowExplain(false);
      setConfirming(false);
    }
  }, [visible, opacity, scale]);

  const backdropStyle = useAnimatedStyle(() => ({ opacity: opacity.value }));
  const cardStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }],
  }));

  const nothingChanged =
    diff !== null &&
    !loading &&
    diff.added === 0 &&
    diff.removed === 0 &&
    diff.rescheduled === 0;

  return (
    <Modal animationType="none" transparent visible={visible} onRequestClose={onClose}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={styles.backdropPress} onPress={onClose} />
      </Animated.View>
      <View pointerEvents="box-none" style={styles.wrap}>
        <Animated.View style={[styles.card, cardStyle]}>
          <View style={styles.iconCircle}>
            <RefreshCw size={22} color={Palette.forest} strokeWidth={1.8} />
          </View>
          <Text style={styles.title}>Refresh Plan</Text>
          <Text style={styles.subtitle}>for {homeName}</Text>

          {loading || diff === null ? (
            <View style={styles.loadingBox}>
              <ActivityIndicator size="small" color={Palette.forest} />
              <Text style={styles.loadingText}>Calculating changes…</Text>
            </View>
          ) : nothingChanged ? (
            <View style={styles.nothingBox}>
              <Check size={16} color={Palette.moss} strokeWidth={2.2} />
              <Text style={styles.nothingText}>
                Nothing to update — your plan is already in sync with your home
                profile.
              </Text>
            </View>
          ) : (
            <ScrollView
              style={styles.scroll}
              contentContainerStyle={{ paddingBottom: Spacing.three }}
              showsVerticalScrollIndicator={true}>
              {diff.added > 0 ? (
                <Section
                  Icon={Plus}
                  iconColor={Palette.moss}
                  iconBg="rgba(106,141,90,0.16)"
                  label={`Items to add · ${diff.added}`}
                  titles={diff.addedTitles}
                />
              ) : null}
              {diff.removed > 0 ? (
                <Section
                  Icon={Minus}
                  iconColor={Palette.clay}
                  iconBg="rgba(176,95,60,0.15)"
                  label={`Items to remove · ${diff.removed}`}
                  titles={diff.removedTitles}
                  subtle="No longer applies to your home profile."
                />
              ) : null}
              {diff.rescheduled > 0 ? (
                <Section
                  Icon={RefreshCw}
                  iconColor={Palette.honeyDeep}
                  iconBg="rgba(225,163,61,0.18)"
                  label={`Items to reschedule · ${diff.rescheduled}`}
                  titles={diff.rescheduledTitles}
                  subtle="Existing tasks will shift to better dates based on your current attributes."
                />
              ) : null}
            </ScrollView>
          )}

          {/* "How does this work?" expandable. Closed by default so the
              preview reads as the first action; users who want the
              full long-form rationale can flip it open. */}
          <Pressable
            onPress={() => setShowExplain((v) => !v)}
            style={({ pressed }) => [
              styles.explainToggle,
              pressed && { opacity: 0.7 },
            ]}
            accessibilityRole="button"
            accessibilityLabel={
              showExplain ? 'Hide replan explanation' : 'Show replan explanation'
            }>
            <Text style={styles.explainToggleText}>
              {showExplain ? 'Hide explanation' : 'How does this work?'}
            </Text>
            {showExplain ? (
              <ChevronUp size={14} color={Palette.forest} strokeWidth={2.2} />
            ) : (
              <ChevronDown size={14} color={Palette.forest} strokeWidth={2.2} />
            )}
          </Pressable>
          {showExplain ? (
            <View style={styles.explainPanel}>
              <Text style={styles.explainHead}>What Replan does</Text>
              <Text style={styles.explainBody}>
                Rebuilds the next 12 months of tasks using your current home
                attributes (build year, systems, lifestyle, climate).
              </Text>
              <Text style={styles.explainHead}>What stays</Text>
              <Text style={styles.explainBody}>
                Every completed task, every snoozed task, and every custom
                task you added by hand. We never delete those.
              </Text>
              <Text style={styles.explainHead}>What changes</Text>
              <Text style={styles.explainBody}>
                Scheduled (not-yet-done) tasks get a fresh due-date pass.
                New tasks may appear based on attributes you added since
                the last plan, and rules that no longer apply drop off
                the schedule. Safe to run anytime.
              </Text>
            </View>
          ) : null}

          {/* Action button — primary "Replan now" when there's a diff
              to apply, plain "Close" when nothing would change. Calling
              onConfirm with a nothing-to-do diff would just thrash
              the task table for no user benefit. */}
          {nothingChanged || onConfirm === undefined ? (
            <Pressable
              onPress={onClose}
              style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.85 }]}>
              <Text style={styles.closeBtnText}>Close</Text>
            </Pressable>
          ) : (
            <Pressable
              onPress={() => {
                if (confirming || loading) return;
                setConfirming(true);
                onConfirm();
              }}
              disabled={confirming || loading}
              style={({ pressed }) => [
                styles.closeBtn,
                (confirming || loading) && { opacity: 0.6 },
                pressed && { opacity: 0.85 },
              ]}>
              <Text style={styles.closeBtnText}>
                {confirming ? 'Replanning…' : 'Replan now'}
              </Text>
            </Pressable>
          )}

          <Pressable
            onPress={onClose}
            hitSlop={10}
            style={({ pressed }) => [styles.cornerClose, pressed && { opacity: 0.6 }]}
            accessibilityLabel="Close">
            <X size={16} color={Palette.inkSoft} strokeWidth={2} />
          </Pressable>
        </Animated.View>
      </View>
    </Modal>
  );
}

function Section({
  Icon,
  iconColor,
  iconBg,
  label,
  titles,
  subtle,
}: {
  Icon: typeof Plus;
  iconColor: string;
  iconBg: string;
  label: string;
  titles: string[];
  subtle?: string;
}) {
  return (
    <View style={styles.section}>
      <View style={styles.sectionHead}>
        <View style={[styles.sectionIcon, { backgroundColor: iconBg }]}>
          <Icon size={13} color={iconColor} strokeWidth={2.2} />
        </View>
        <Text style={styles.sectionLabel}>{label}</Text>
      </View>
      {subtle ? <Text style={styles.sectionSubtle}>{subtle}</Text> : null}
      <View style={styles.titleList}>
        {titles.map((t, i) => (
          <View key={`${t}-${i}`} style={styles.titleRow}>
            <View style={styles.bullet} />
            <Text style={styles.titleText} numberOfLines={2}>
              {t}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15,24,18,0.6)',
  },
  backdropPress: { flex: 1 },
  wrap: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    maxHeight: '85%',
    backgroundColor: Palette.sand,
    borderRadius: 24,
    paddingHorizontal: 22,
    paddingTop: 28,
    paddingBottom: 18,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 10 },
    elevation: 18,
  },
  cornerClose: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'rgba(0,0,0,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    marginTop: 14,
    fontFamily: Fonts.serif,
    fontSize: 22,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  subtitle: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
  },
  scroll: {
    alignSelf: 'stretch',
    marginTop: Spacing.three,
    maxHeight: 360,
  },
  loadingBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: Spacing.three,
    padding: 14,
    borderRadius: Radius.lg,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  loadingText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
  },
  section: {
    marginTop: Spacing.three,
    padding: 12,
    borderRadius: Radius.lg,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  sectionHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sectionIcon: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  sectionSubtle: {
    marginTop: 6,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    lineHeight: 16,
  },
  titleList: {
    marginTop: 8,
    gap: 6,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  bullet: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginTop: 7,
    backgroundColor: Palette.inkFaint,
  },
  titleText: {
    flex: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.ink,
    lineHeight: 18,
  },
  nothingBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: Spacing.three,
    padding: 14,
    borderRadius: Radius.lg,
    backgroundColor: '#EAF0EA',
  },
  nothingText: {
    flex: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.forest,
    lineHeight: 18,
  },
  explainToggle: {
    marginTop: Spacing.three,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 6,
  },
  explainToggleText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.forest,
    letterSpacing: 0.2,
  },
  explainPanel: {
    alignSelf: 'stretch',
    marginTop: 8,
    padding: 14,
    borderRadius: Radius.lg,
    backgroundColor: '#F4F7F2',
    borderWidth: 1,
    borderColor: Palette.line,
    gap: 4,
  },
  explainHead: {
    marginTop: 8,
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.forest,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  explainBody: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    color: Palette.inkSoft,
    lineHeight: 17,
  },
  closeBtn: {
    marginTop: Spacing.three,
    alignSelf: 'stretch',
    paddingVertical: 12,
    borderRadius: Radius.lg,
    backgroundColor: Palette.forest,
    alignItems: 'center',
  },
  closeBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.cream,
    letterSpacing: 0.3,
  },
});
